import {
  APPEARANCE_LABELS,
  BODY_COLORS,
  BODY_TYPES,
  HAIR_COLORS,
  HAIR_STYLES,
  OUTFITS,
  SKIN_TONES,
  type Appearance,
  type AppearanceChoice,
} from "@townsy/contracts";

/**
 * 2D avatar preview model for the sign-up / "Edit avatar" builder (ADR-0004 #12). Pure data so the
 * mapping from every choice to what the SVG draws is unit-tested. Colours mirror the renderer's
 * palette closely enough to recognise the 3D avatar; the 3D town is the authority on looks.
 */

export const SHIRT_HEX: Record<AppearanceChoice["bodyColor"], string> = {
  cobalt: "#2851D9",
  coral: "#F0715B",
  moss: "#5F8F3E",
  amber: "#D99A2B",
  plum: "#7D44A6",
  teal: "#1E9C9C",
  slate: "#5C6C86",
  rose: "#D95A8F",
  white: "#F4F4F2",
  black: "#23252B",
};

export const SKIN_HEX: Record<AppearanceChoice["skinTone"], string> = {
  s1: "#F7E1C9",
  s2: "#EAC39F",
  s3: "#D4A277",
  s4: "#AD7646",
  s5: "#7E5031",
  s6: "#52311E",
};

export const HAIR_HEX: Record<AppearanceChoice["hairColor"], string> = {
  black: "#1F1F24",
  brown: "#5A3820",
  auburn: "#8C3F1E",
  blonde: "#D9B36A",
  red: "#B8321F",
  grey: "#A8A49E",
  blue: "#2F6FD6",
  pink: "#E27AAE",
};

const DENIM = "#3F5B8C";
const SUIT = "#2B2F3C";
const SHOE = "#2A2A30";

/** Every choice group the builder offers, in display order, with the contract's labels. */
export const APPEARANCE_GROUPS = [
  { field: "bodyType", legend: "Body", options: BODY_TYPES, labels: APPEARANCE_LABELS.bodyType, swatch: null },
  { field: "bodyColor", legend: "Shirt color", options: BODY_COLORS, labels: APPEARANCE_LABELS.bodyColor, swatch: SHIRT_HEX },
  { field: "skinTone", legend: "Skin tone", options: SKIN_TONES, labels: APPEARANCE_LABELS.skinTone, swatch: SKIN_HEX },
  { field: "hairColor", legend: "Hair color", options: HAIR_COLORS, labels: APPEARANCE_LABELS.hairColor, swatch: HAIR_HEX },
  { field: "hairStyle", legend: "Hair style", options: HAIR_STYLES, labels: APPEARANCE_LABELS.hairStyle, swatch: null },
  { field: "outfit", legend: "Outfit", options: OUTFITS, labels: APPEARANCE_LABELS.outfit, swatch: null },
] as const;

export type AppearanceField = (typeof APPEARANCE_GROUPS)[number]["field"];

export const DEFAULT_CHOICE: AppearanceChoice = {
  bodyType: "male",
  bodyColor: "cobalt",
  skinTone: "s3",
  hairStyle: "short",
  hairColor: "brown",
  outfit: "casual",
};

/** Strip the server-assigned version: what the builder edits and what `PUT /me/appearance` accepts. */
export function choiceFromAppearance(a: Appearance | null | undefined): AppearanceChoice {
  if (!a) return { ...DEFAULT_CHOICE };
  return {
    bodyType: a.bodyType,
    bodyColor: a.bodyColor,
    skinTone: a.skinTone,
    hairStyle: a.hairStyle,
    hairColor: a.hairColor,
    outfit: a.outfit,
  };
}

/**
 * Apply one builder click. Switching the body to Male swaps a dress for "casual" and long/bun hair for "short"
 * (founder 2026-10-12: a male body in a dress read as a woman); every other click, and any later choice, is
 * applied exactly as made, so any combination is still possible on purpose.
 */
export function applyChoice(value: AppearanceChoice, field: AppearanceField, option: string): AppearanceChoice {
  const next = { ...value, [field]: option } as AppearanceChoice;
  if (field === "bodyType" && option === "male" && value.bodyType !== "male") {
    if (next.outfit === "dress") next.outfit = "casual";
    if (next.hairStyle === "long" || next.hairStyle === "bun") next.hairStyle = "short";
  }
  return next;
}

/** A guest's random starter look shown at sign-up, made coherent with its body type (never used for saved resident looks). */
export function coherentStarter(c: AppearanceChoice): AppearanceChoice {
  if (c.bodyType !== "male") return c;
  return {
    ...c,
    outfit: c.outfit === "dress" ? "casual" : c.outfit,
    hairStyle: c.hairStyle === "long" || c.hairStyle === "bun" ? "short" : c.hairStyle,
  };
}

export function describeChoice(c: AppearanceChoice): string {
  const L = APPEARANCE_LABELS;
  const hair = c.hairStyle === "none" ? "no hair" : `${L.hairColor[c.hairColor].toLowerCase()} ${L.hairStyle[c.hairStyle].toLowerCase()} hair`;
  return `${L.bodyType[c.bodyType]} avatar, skin ${L.skinTone[c.skinTone].toLowerCase()}, ${L.bodyColor[c.bodyColor].toLowerCase()} shirt, ${L.outfit[c.outfit].toLowerCase()} outfit, ${hair}`;
}

export interface AvatarPreviewModel {
  skin: string;
  shirt: string;
  hair: string;
  hairStyle: AppearanceChoice["hairStyle"];
  /** Cap brim/crown colour when the hair style is a cap (cap sits over the hair colour fringe). */
  cap: string | null;
  /** Half widths in SVG units (slim proportions; female narrower shoulders + defined waist). */
  shoulderHalf: number;
  waistHalf: number;
  hipHalf: number;
  /** Jacket over the shirt (suit). */
  jacket: string | null;
  /** Hood behind the head (hoodie). */
  hood: boolean;
  /** Skirt of the dress in the shirt colour; null = trousers/shorts. */
  skirt: string | null;
  legs: string;
  /** Legs below shorts/skirt show skin. */
  shortLegs: boolean;
  shoes: string;
  /** Short accessible description of the whole look. */
  description: string;
}

export function avatarPreviewModel(c: AppearanceChoice): AvatarPreviewModel {
  const female = c.bodyType === "female";
  const shirt = SHIRT_HEX[c.bodyColor];
  const skin = SKIN_HEX[c.skinTone];
  const outfit = c.outfit;
  return {
    skin,
    shirt,
    hair: HAIR_HEX[c.hairColor],
    hairStyle: c.hairStyle,
    cap: c.hairStyle === "cap" ? shirt : null,
    shoulderHalf: female ? 13 : 16,
    waistHalf: female ? 9.5 : 12.5,
    hipHalf: female ? 13 : 12.5,
    jacket: outfit === "suit" ? SUIT : null,
    hood: outfit === "hoodie",
    skirt: outfit === "dress" ? shirt : null,
    legs: outfit === "sport" || outfit === "dress" ? skin : outfit === "suit" ? SUIT : DENIM,
    shortLegs: outfit === "sport" || outfit === "dress",
    shoes: outfit === "sport" ? "#F7F7F7" : SHOE,
    description: describeChoice(c),
  };
}
